import React, { useState } from 'react';
import './OppgaveProdusent.css';
import crmApi from '../api/crm';
import * as pdfjsLib from 'pdfjs-dist';

// Konfigurer PDF.js worker (bruker jsdelivr CDN som er mer pålitelig)
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

/**
 * Oppgave-produsent
 * Last opp dokument → AI analyserer → Godkjenn oppgaver
 */
function OppgaveProdusent({ onOppgaverOpprettet }) {
  const [step, setStep] = useState('upload'); // 'upload', 'analyzing', 'review', 'success'
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentText, setDocumentText] = useState('');
  const [analyseResultat, setAnalyseResultat] = useState(null);
  const [analyseId, setAnalyseId] = useState(null);
  const [selectedOppgaver, setSelectedOppgaver] = useState([]);
  const [redigerteOppgaver, setRedigerteOppgaver] = useState({});
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Håndter fil-valg
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  // Håndter drag-and-drop
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  // Ekstraher tekst fra PDF
  const extractTextFromPDF = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      let fullText = '';

      // Les alle sider
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        fullText += pageText + '\n\n';
      }

      return fullText.trim();
    } catch (err) {
      console.error('PDF parsing error:', err);
      throw new Error('Kunne ikke lese PDF-fil. Prøv å konvertere til tekst først.');
    }
  };

  // Prosesser fil
  const processFile = async (file) => {
    setSelectedFile(file);
    setError(null);

    try {
      // For tekst-filer: Les direkte
      if (file.type === 'text/plain') {
        const text = await file.text();
        setDocumentText(text);
      }
      // For PDF: Ekstraher tekst med pdf.js
      else if (file.type === 'application/pdf') {
        const text = await extractTextFromPDF(file);
        setDocumentText(text);
      }
      else {
        setError('Kun PDF og tekst-filer støttes');
        return;
      }
    } catch (err) {
      setError(err.message || 'Kunne ikke lese filen');
      setSelectedFile(null);
    }
  };

  // Analyser dokument
  const analyserDokument = async () => {
    if (!documentText) {
      setError('Ingen dokument-tekst tilgjengelig');
      return;
    }

    setStep('analyzing');
    setError(null);

    try {
      const response = await crmApi.request('/api/oppgave-produsent/analyser', {
        method: 'POST',
        body: JSON.stringify({
          dokument_tekst: documentText,
          filnavn: selectedFile?.name || 'dokument.txt',
          filtype: selectedFile?.type || 'text/plain',
          sak_id: null // TODO: Kan velges fra dropdown
        })
      });

      console.log('📊 Analyse-resultat (v2):', response);

      // Sjekk duplikat
      if (response.duplikat_varsel?.er_duplikat) {
        // Håndter 100% duplikat (eksisterende) eller semantisk lik (lignende)
        const duplikat = response.duplikat_varsel.eksisterende || (response.duplikat_varsel.lignende && response.duplikat_varsel.lignende[0]);

        if (duplikat) {
          const confirm = window.confirm(
            `⚠️ Dette dokumentet ${duplikat.likhet === 100 ? 'er identisk med' : `ligner ${duplikat.likhet}% på`} "${duplikat.filnavn}" lastet opp ${new Date(duplikat.opprettet_dato).toLocaleDateString()}.\n\nVil du fortsette likevel?`
          );
          if (!confirm) {
            setStep('upload');
            return;
          }
        }
      }

      // Hvis 100% duplikat og ingen oppgaver, vis melding og gå tilbake
      if (response.duplikat_varsel?.er_duplikat && !response.foreslatte_oppgaver) {
        alert('Dette dokumentet er allerede analysert. Kan ikke opprette nye oppgaver fra et duplikat.');
        setStep('upload');
        return;
      }

      setAnalyseResultat(response);
      setAnalyseId(response.analyse_id);

      // Auto-velg alle oppgaver
      setSelectedOppgaver(response.foreslatte_oppgaver?.map((_, i) => i) || []);

      setStep('review');

    } catch (err) {
      console.error('Analyse feilet:', err);
      setError(err.message || 'Analyse feilet');
      setStep('upload');
    }
  };

  // Godkjenn og opprett oppgaver
  const godkjennOppgaver = async () => {
    if (!analyseId) {
      setError('Mangler analyse-ID');
      return;
    }

    setError(null);

    try {
      const response = await crmApi.request('/api/oppgave-produsent/godkjenn', {
        method: 'POST',
        body: JSON.stringify({
          analyse_id: analyseId,
          godkjente_oppgaver: selectedOppgaver,
          redigerte_oppgaver: Object.entries(redigerteOppgaver).map(([index, endringer]) => ({
            index: parseInt(index),
            endringer
          }))
        })
      });

      console.log('✅ Oppgaver opprettet:', response);

      setStep('success');

      // Callback til parent (for å oppdatere oppgave-liste)
      if (onOppgaverOpprettet) {
        onOppgaverOpprettet(response.opprettet_oppgaver);
      }

    } catch (err) {
      console.error('Opprettelse feilet:', err);
      setError(err.message || 'Kunne ikke opprette oppgaver');
    }
  };

  // Toggle oppgave-valg
  const toggleOppgave = (index) => {
    setSelectedOppgaver(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  // Rediger oppgave
  const redigerOppgave = (index, felt, verdi) => {
    setRedigerteOppgaver(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        [felt]: verdi
      }
    }));
  };

  // Reset til start
  const reset = () => {
    setStep('upload');
    setSelectedFile(null);
    setDocumentText('');
    setAnalyseResultat(null);
    setAnalyseId(null);
    setSelectedOppgaver([]);
    setRedigerteOppgaver({});
    setError(null);
  };

  return (
    <div className="oppgave-produsent">
      <div className="oppgave-produsent-header">
        <h2>📄 Oppgave-produsent</h2>
        <p>Last opp dokument og få generert oppgaver automatisk</p>
      </div>

      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      {/* STEG 1: Last opp */}
      {step === 'upload' && (
        <div className="upload-section">
          <div
            className={`dropzone ${isDragging ? 'dragging' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <div className="dropzone-content">
              <span className="upload-icon">📁</span>
              <h3>Dra dokument hit</h3>
              <p>eller</p>
              <div className="file-input-label">
                <input
                  id="file-upload-input"
                  type="file"
                  accept=".txt,.pdf"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <button
                  className="btn-primary"
                  onClick={() => document.getElementById('file-upload-input').click()}
                  type="button"
                >
                  Velg fil
                </button>
              </div>
              <p className="upload-hint">Støtter: PDF, TXT</p>
            </div>
          </div>

          {selectedFile && documentText && (
            <div className="selected-file">
              <h4>📄 {selectedFile.name}</h4>
              <p>{(selectedFile.size / 1024).toFixed(1)} KB</p>
              <textarea
                value={documentText}
                onChange={(e) => setDocumentText(e.target.value)}
                rows={10}
                placeholder="Dokumentinnhold..."
                className="document-preview"
              />
              <button className="btn-primary" onClick={analyserDokument}>
                🤖 Analyser dokument
              </button>
            </div>
          )}
        </div>
      )}

      {/* STEG 2: Analyserer */}
      {step === 'analyzing' && (
        <div className="analyzing-section">
          <div className="spinner"></div>
          <h3>🔄 Analyserer dokument...</h3>
          <p>AI identifiserer oppgaver, frister og risikoer</p>
        </div>
      )}

      {/* STEG 3: Gjennomgå resultat */}
      {step === 'review' && analyseResultat && (
        <div className="review-section">
          <div className="analyse-header">
            <h3>📊 Analyse-resultat</h3>
            <div className="document-type">
              <strong>Type:</strong> {analyseResultat.dokumenttype}
              <span className="confidence">
                ({Math.round(analyseResultat.konfidensintervall * 100)}% sikker)
              </span>
            </div>
            <p className="sammendrag">{analyseResultat.sammendrag}</p>
          </div>

          {/* Frister */}
          {analyseResultat.frister?.length > 0 && (
            <div className="frister-section">
              <h4>📅 Frister</h4>
              {analyseResultat.frister.map((frist, i) => (
                <div key={i} className={`frist-item ${frist.kritisk ? 'kritisk' : ''}`}>
                  <span className="frist-beskrivelse">{frist.beskrivelse}</span>
                  <span className="frist-dato">{frist.dato}</span>
                  {frist.dager_igjen !== undefined && (
                    <span className="frist-countdown">({frist.dager_igjen} dager)</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Oppgaver */}
          <div className="oppgaver-section">
            <h4>✅ Foreslåtte oppgaver ({analyseResultat.foreslatte_oppgaver?.length || 0})</h4>
            <div className="oppgaver-liste">
              {analyseResultat.foreslatte_oppgaver?.map((oppgave, i) => {
                const isSelected = selectedOppgaver.includes(i);
                const redigert = redigerteOppgaver[i] || {};

                return (
                  <div key={i} className={`oppgave-item ${isSelected ? 'selected' : ''}`}>
                    <label className="oppgave-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleOppgave(i)}
                      />
                      <div className="oppgave-details">
                        <input
                          type="text"
                          value={redigert.tittel || oppgave.tittel}
                          onChange={(e) => redigerOppgave(i, 'tittel', e.target.value)}
                          className="oppgave-tittel"
                          disabled={!isSelected}
                        />
                        <textarea
                          value={redigert.beskrivelse || oppgave.beskrivelse}
                          onChange={(e) => redigerOppgave(i, 'beskrivelse', e.target.value)}
                          rows={2}
                          className="oppgave-beskrivelse"
                          disabled={!isSelected}
                        />
                        <div className="oppgave-meta">
                          <span className={`prioritet prioritet-${oppgave.prioritet}`}>
                            {oppgave.prioritet}
                          </span>
                          {oppgave.forfallsdato && (
                            <span className="forfallsdato">📅 {oppgave.forfallsdato}</span>
                          )}
                          {oppgave.estimert_timer && (
                            <span className="estimert-tid">⏱️ {oppgave.estimert_timer}t</span>
                          )}
                        </div>
                      </div>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Spørsmål til klient */}
          {analyseResultat.sporsmal_til_klient?.length > 0 && (
            <div className="sporsmal-section">
              <h4>❓ Spørsmål til klient</h4>
              <ul>
                {analyseResultat.sporsmal_til_klient.map((item, i) => (
                  <li key={i} className={`prioritet-${item.prioritet}`}>
                    {item.sporsmal}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Risikoer */}
          {analyseResultat.risikoer?.length > 0 && (
            <div className="risikoer-section">
              <h4>⚠️ Risikoområder</h4>
              <ul>
                {analyseResultat.risikoer.map((risiko, i) => (
                  <li key={i}>{risiko}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="review-actions">
            <button className="btn-secondary" onClick={reset}>
              ← Tilbake
            </button>
            <button
              className="btn-primary"
              onClick={godkjennOppgaver}
              disabled={selectedOppgaver.length === 0}
            >
              ✅ Opprett {selectedOppgaver.length} oppgaver
            </button>
          </div>
        </div>
      )}

      {/* STEG 4: Suksess */}
      {step === 'success' && (
        <div className="success-section">
          <div className="success-icon">✅</div>
          <h3>Oppgaver opprettet!</h3>
          <p>{selectedOppgaver.length} nye oppgaver lagt til</p>
          <button className="btn-primary" onClick={reset}>
            📄 Last opp nytt dokument
          </button>
        </div>
      )}
    </div>
  );
}

export default OppgaveProdusent;
